import {
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  message,
} from 'antd';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {FaFileInvoiceDollar} from 'react-icons/fa';
import {useSelector} from 'react-redux';

import {RqUserLookupAllRole} from '../../../ReactQuery/apis';
import {selectAuth} from '../../../Redux/selectors';

import {
  createBill,
  deleteBill,
  getBills,
  updateBill,
} from './billApi';
import BillModal from './BillModal';

const EMPTY_BILL_ITEM = {
  itemName: '',
  amount: 0,
  type: undefined,
  modeOfPayment: null,
};

const getBillId = bill =>
  bill?.billId ?? bill?.id ?? bill?._id ?? null;

const calculateTotal = items =>
  Array.isArray(items)
    ? items.reduce(
      (total, item) =>
        total + Number(item?.amount || 0),
      0,
    )
    : 0;

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  (error?.response?.status === 401 &&
    'Your session has expired. Please log in again.') ||
  (error?.response?.status === 403 &&
    'You are not authorized to perform this action.') ||
  (error?.response?.status === 404 &&
    'The requested bill was not found.') ||
  (error?.code === 'ERR_NETWORK' &&
    'Unable to connect to the backend server.') ||
  error?.message ||
  fallback;

const PatientBillScreen = () => {
  const [form] = Form.useForm();
  const {tokenData: token} = useSelector(selectAuth);

  const {
    data: patients = [],
    isFetching: patientsLoading,
  } = RqUserLookupAllRole(
    'PATIENT',
    token,
    {
      enabled: Boolean(token),
    },
  );

  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingBillId, setDeletingBillId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState();

  const loadBills = useCallback(async () => {
    if (!token) {
      setBills([]);
      return;
    }

    try {
      setLoading(true);

      const response = await getBills(token);

      setBills(
        Array.isArray(response)
          ? response
          : response?.data ||
            response?.content ||
            [],
      );
    } catch (error) {
      console.error(
        'Error loading bills:',
        error,
      );

      message.error(
        getErrorMessage(
          error,
          'Failed to load patient bills.',
        ),
      );

      setBills([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadBills();
  }, [loadBills]);

  const resetBillForm = useCallback(() => {
    form.resetFields();

    form.setFieldsValue({
      patientId: undefined,
      modeOfPayment: undefined,
      billItems: [
        {
          ...EMPTY_BILL_ITEM,
        },
      ],
      totalAmount: 0,
    });
  }, [form]);

  const handleAddBill = () => {
    setEditingBill(null);
    resetBillForm();
    setIsModalOpen(true);
  };

  const handleEditBill = bill => {
    const billId = getBillId(bill);

    if (!billId) {
      message.error(
        'Unable to edit this bill because the bill ID is missing.',
      );
      return;
    }

    const billItems =
      Array.isArray(bill?.billItems) &&
      bill.billItems.length > 0
        ? bill.billItems.map(item => ({
          itemName: item?.itemName || '',
          amount: Number(item?.amount || 0),
          type: item?.type,
          modeOfPayment: null,
        }))
        : [
          {
            ...EMPTY_BILL_ITEM,
          },
        ];

    setEditingBill(bill);
    form.resetFields();

    form.setFieldsValue({
      patientId: bill?.patientId,
      modeOfPayment: bill?.modeOfPayment,
      billItems,
      totalAmount: calculateTotal(
        billItems,
      ),
    });

    setIsModalOpen(true);
  };

  const handleCancel = () => {
    if (saving) {
      return;
    }

    setIsModalOpen(false);
    setEditingBill(null);
    form.resetFields();
  };

  const handleSubmit = async values => {
    if (!token) {
      message.error(
        'You are not logged in. Please log in again.',
      );
      return;
    }

    const billItems = Array.isArray(
      values?.billItems,
    )
      ? values.billItems.map(item => ({
        itemName: String(
          item?.itemName || '',
        ).trim(),
        amount: Number(
          item?.amount || 0,
        ),
        type: item?.type,
        modeOfPayment: null,
      }))
      : [];

    const payload = {
      patientId:
        values?.patientId || null,
      modeOfPayment:
        values?.modeOfPayment || null,
      billItems,
      totalAmount:
        calculateTotal(billItems),
    };

    console.log(
      '========== FINAL API PAYLOAD ==========',
    );
    console.log(
      'Patient ID:',
      payload.patientId,
    );
    console.log(
      'Mode Of Payment:',
      payload.modeOfPayment,
    );
    console.log(
      'Bill Items:',
      payload.billItems,
    );
    console.log(
      'Total Amount:',
      payload.totalAmount,
    );
    console.log(
      'Complete Payload:',
      payload,
    );
    console.log(
      '=======================================',
    );

    try {
      setSaving(true);

      if (editingBill) {
        const billId =
          getBillId(editingBill);

        if (!billId) {
          message.error(
            'Unable to update this bill because the bill ID is missing.',
          );
          return;
        }

        await updateBill(
          billId,
          payload,
          token,
        );

        message.success(
          'Bill updated successfully.',
        );
      } else {
        await createBill(
          payload,
          token,
        );

        message.success(
          'Bill created successfully.',
        );
      }

      setIsModalOpen(false);
      setEditingBill(null);
      form.resetFields();

      await loadBills();
    } catch (error) {
      console.error(
        'Error saving bill:',
        error,
      );

      message.error(
        getErrorMessage(
          error,
          editingBill
            ? 'Failed to update bill.'
            : 'Failed to create bill.',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBill = async billId => {
    if (!billId) {
      message.error(
        'Unable to delete this bill because the bill ID is missing.',
      );
      return;
    }

    if (!token) {
      message.error(
        'You are not logged in. Please log in again.',
      );
      return;
    }

    try {
      setDeletingBillId(billId);

      await deleteBill(
        billId,
        token,
      );

      message.success(
        'Bill deleted successfully.',
      );

      await loadBills();
    } catch (error) {
      console.error(
        'Error deleting bill:',
        error,
      );

      message.error(
        getErrorMessage(
          error,
          'Failed to delete bill.',
        ),
      );
    } finally {
      setDeletingBillId(null);
    }
  };

  const filteredAndSortedBills = useMemo(() => {
    let result = [...bills];

    const search =
      searchText
        .trim()
        .toLowerCase();

    if (search) {
      result = result.filter(bill => {
        const itemsText =
          (bill?.billItems || [])
            .map(item =>
              [
                item?.itemName,
                item?.type,
                item?.amount,
              ]
                .filter(
                  value =>
                    value != null,
                )
                .join(' '),
            )
            .join(' ')
            .toLowerCase();

        return (
          [
            bill?.patientName,
            bill?.totalAmount,
            bill?.modeOfPayment,
            getBillId(bill),
          ].some(value =>
            String(
              value || '',
            )
              .toLowerCase()
              .includes(search),
          ) ||
          itemsText.includes(search)
        );
      });
    }

    if (!sortBy) {
      return result;
    }

    return result.sort((a, b) => {
      switch (sortBy) {
        case 'patientNameAsc':
          return String(
            a?.patientName || '',
          )
            .toLowerCase()
            .localeCompare(
              String(
                b?.patientName || '',
              ).toLowerCase(),
            );

        case 'patientNameDesc':
          return String(
            b?.patientName || '',
          )
            .toLowerCase()
            .localeCompare(
              String(
                a?.patientName || '',
              ).toLowerCase(),
            );

        case 'amountAsc':
          return (
            Number(
              a?.totalAmount || 0,
            ) -
            Number(
              b?.totalAmount || 0,
            )
          );

        case 'amountDesc':
          return (
            Number(
              b?.totalAmount || 0,
            ) -
            Number(
              a?.totalAmount || 0,
            )
          );

        case 'dateAsc':
          return (
            new Date(
              a?.createdAt || 0,
            ) -
            new Date(
              b?.createdAt || 0,
            )
          );

        case 'dateDesc':
          return (
            new Date(
              b?.createdAt || 0,
            ) -
            new Date(
              a?.createdAt || 0,
            )
          );

        default:
          return 0;
      }
    });
  }, [
    bills,
    searchText,
    sortBy,
  ]);

  const columns = [
    {
      title: 'Patient Name',
      key: 'patient',
      render: (_, record) => (
        <span
          className={
            'font-semibold text-gray-800'
          }
        >
          {record?.patientName ||
            'N/A'}
        </span>
      ),
    },
    {
      title: 'Bill Items',
      key: 'billItems',
      render: (_, record) => {
        const items =
          Array.isArray(
            record?.billItems,
          )
            ? record.billItems
            : [];

        if (!items.length) {
          return (
            <span
              className={
                'text-gray-400'
              }
            >
              No items
            </span>
          );
        }

        return (
          <div
            className={
              'space-y-1'
            }
          >
            {items.map(
              (
                item,
                index,
              ) => (
                <div
                  key={`${item?.itemName || 'item'}-${index}`}
                  className={
                    'flex items-center justify-between gap-4'
                  }
                >
                  <span>
                    {item?.itemName ||
                      'Unnamed item'}
                  </span>

                  <span
                    className={
                      'text-gray-500'
                    }
                  >
                    ₹
                    {Number(
                      item?.amount ||
                        0,
                    ).toFixed(2)}
                  </span>
                </div>
              ),
            )}
          </div>
        );
      },
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: amount => (
        <span
          className={
            'font-semibold text-primary-700'
          }
        >
          ₹
          {Number(
            amount || 0,
          ).toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Payment Mode',
      dataIndex: 'modeOfPayment',
      key: 'modeOfPayment',
      render: mode => (
        <span
          className={
            'font-medium text-gray-700'
          }
        >
          {mode || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: date =>
        date
          ? new Date(
            date,
          ).toLocaleString(
            'en-IN',
          )
          : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => {
        const billId =
          getBillId(record);

        const isDeleting =
          deletingBillId === billId;

        return (
          <Space
            size={4}
          >
            <Button
              type={'link'}
              size={'small'}
              icon={
                <EditOutlined />
              }
              disabled={
                !billId ||
                Boolean(
                  deletingBillId,
                )
              }
              onClick={() =>
                handleEditBill(
                  record,
                )
              }
              className={
                'text-primary-600'
              }
            >
              Edit
            </Button>

            <Popconfirm
              title={
                'Delete this bill?'
              }
              description={
                'Are you sure you want to delete this bill?'
              }
              okText={'Yes'}
              cancelText={'No'}
              okButtonProps={{
                danger: true,
                loading:
                  isDeleting,
              }}
              disabled={
                !billId ||
                Boolean(
                  deletingBillId,
                )
              }
              onConfirm={() =>
                handleDeleteBill(
                  billId,
                )
              }
            >
              <Button
                type={'link'}
                danger
                size={'small'}
                icon={
                  <DeleteOutlined />
                }
                loading={
                  isDeleting
                }
                disabled={
                  !billId ||
                  Boolean(
                    deletingBillId &&
                    !isDeleting,
                  )
                }
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div
      className={
        'w-full p-4 md:p-6'
      }
    >
      <div
        className={
          'mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between'
        }
      >
        <div
          className={
            'flex items-center gap-3'
          }
        >
          <div
            className={
              'flex h-10 w-12 items-center justify-center rounded-xl text-white'
            }
            style={{
              background:
                'linear-gradient(135deg, #b69c66, #d4bc87)',
            }}
          >
            <FaFileInvoiceDollar
              style={{
                fontSize: 22,
              }}
            />
          </div>

          <h1
            className={
              'title !mb-0'
            }
          >
            Patient Bills
          </h1>
        </div>

        <Button
          type={'primary'}
          size={'large'}
          onClick={
            handleAddBill
          }
          disabled={!token}
          className={
            'btn-primary'
          }
        >
          + Create Bill
        </Button>
      </div>

      <div
        className={
          'mb-5'
        }
      >
        <div
          className={
            'mb-2 flex items-center gap-2'
          }
        >
          <FilterOutlined
            className={
              'text-primary-500'
            }
          />

          <span
            className={
              'text-sm font-medium text-gray-700'
            }
          >
            Filters
          </span>
        </div>

        <div
          className={
            'flex flex-col gap-2 md:flex-row md:items-center'
          }
        >
          <Input
            allowClear
            prefix={(
              <SearchOutlined
                className={
                  'text-gray-400'
                }
              />
            )}
            placeholder={
              'Search by patient, item, type, payment mode or amount'
            }
            value={
              searchText
            }
            onChange={
              event =>
                setSearchText(
                  event.target
                    .value,
                )
            }
            className={
              'w-full md:w-[320px]'
            }
          />

          <Select
            allowClear
            className={
              'w-full md:w-[250px]'
            }
            placeholder={
              'Sort by'
            }
            value={
              sortBy
            }
            onChange={
              setSortBy
            }
            options={[
              {
                label:
                  'Patient Name: A → Z',
                value:
                  'patientNameAsc',
              },
              {
                label:
                  'Patient Name: Z → A',
                value:
                  'patientNameDesc',
              },
              {
                label:
                  'Total Amount: Low → High',
                value:
                  'amountAsc',
              },
              {
                label:
                  'Total Amount: High → Low',
                value:
                  'amountDesc',
              },
              {
                label:
                  'Created Date: Oldest → Newest',
                value:
                  'dateAsc',
              },
              {
                label:
                  'Created Date: Newest → Oldest',
                value:
                  'dateDesc',
              },
            ]}
          />
        </div>
      </div>

      <div
        className={
          'rounded-xl bg-white p-4 shadow-sm'
        }
      >
        <Table
          rowKey={record =>
            String(
              getBillId(
                record,
              ),
            )
          }
          columns={
            columns
          }
          dataSource={
            filteredAndSortedBills
          }
          loading={
            loading
          }
          bordered
          locale={{
            emptyText:
              searchText
                ? 'No patient bills found for your search.'
                : 'No patient bills found.',
          }}
          pagination={{
            pageSize: 10,
            showSizeChanger:
              true,
            showTotal:
              total =>
                `Total ${total} bills`,
          }}
          scroll={{
            x: 1100,
          }}
        />
      </div>

      <BillModal
        open={
          isModalOpen
        }
        saving={
          saving
        }
        editingBill={
          editingBill
        }
        form={
          form
        }
        patients={
          patients
        }
        patientsLoading={
          patientsLoading
        }
        onCancel={
          handleCancel
        }
        onFinish={
          handleSubmit
        }
      />
    </div>
  );
};

export default PatientBillScreen;
